import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';

/**
 * DialogProvider — promise-based confirm/prompt built on the shared `Modal`.
 *
 * Wiz-parity rule: the product never blocks on a native browser dialog.
 * `window.alert` / `confirm` / `prompt` freeze the page, cannot be styled,
 * are unusable on the embedded views, and — for `prompt` — silently return
 * `null` on cancel, so a `prompt(...) || 'default'` call performs the action
 * anyway. Use `useToast()` for notifications and this hook for input.
 *
 * A single `prompt` takes an array of fields, so a flow that used to chain
 * three `window.prompt` calls becomes ONE modal the user can cancel as a unit.
 *
 *   const { confirm, prompt } = useDialog();
 *   if (!(await confirm({ title: 'Delete key?', danger: true }))) return;
 *   const v = await prompt({ title: 'Add IOC', fields: [{ name: 'value', label: 'Value', required: true }] });
 *   if (!v) return;             // cancelled — do nothing
 *   await api.create(v.value);
 */

export type PromptFieldType = 'text' | 'textarea' | 'number' | 'select' | 'email';

export interface PromptField {
  name: string;
  label: string;
  type?: PromptFieldType;
  /** Options for `type: 'select'`. A select replaces "type one of: a, b, c" prompts. */
  options?: Array<{ value: string; label: string }>;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  /** Helper text under the field. */
  hint?: string;
}

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button + warning icon. Says nothing about reversibility — put
   *  that in `message` when it is actually true. */
  danger?: boolean;
  /** Single-button acknowledgement — used by `info()`. */
  hideCancel?: boolean;
}

export interface PromptOptions {
  title: string;
  message?: React.ReactNode;
  fields: PromptField[];
  submitLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface DialogContextValue {
  /** Resolves true when confirmed, false on cancel/escape/backdrop. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Resolves the field values, or `null` when cancelled. Never a partial result. */
  prompt: (opts: PromptOptions) => Promise<Record<string, string> | null>;
  /**
   * Single-button modal for content too long for a toast (a rule definition,
   * a policy document). For short status messages prefer `useToast`.
   */
  info: (opts: Omit<ConfirmOptions, 'danger' | 'hideCancel'>) => Promise<void>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export const useDialog = (): DialogContextValue => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within a DialogProvider');
  return ctx;
};

type ActiveDialog =
  | { kind: 'confirm'; opts: ConfirmOptions }
  | { kind: 'prompt'; opts: PromptOptions };

const btnBase =
  'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-wiz-purple/40 disabled:opacity-50';

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState<ActiveDialog | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  // Held across renders so close() always settles the promise exactly once.
  const resolverRef = useRef<((v: any) => void) | null>(null);

  const settle = useCallback((result: boolean | Record<string, string> | null) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setActive(null);
    setValues({});
    setTouched(false);
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // A dialog opened while another is up would orphan the first promise.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve as (v: any) => void;
      setActive({ kind: 'confirm', opts });
    });
  }, []);

  const info = useCallback(async (opts: Omit<ConfirmOptions, 'danger' | 'hideCancel'>) => {
    await new Promise<boolean>((resolve) => {
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve as (v: any) => void;
      setActive({ kind: 'confirm', opts: { ...opts, hideCancel: true, confirmLabel: opts.confirmLabel || 'Close' } });
    });
  }, []);

  const prompt = useCallback((opts: PromptOptions) => {
    return new Promise<Record<string, string> | null>((resolve) => {
      if (resolverRef.current) resolverRef.current(null);
      resolverRef.current = resolve as (v: any) => void;
      const initial: Record<string, string> = {};
      opts.fields.forEach((f) => {
        initial[f.name] = f.defaultValue ?? (f.type === 'select' ? f.options?.[0]?.value ?? '' : '');
      });
      setValues(initial);
      setTouched(false);
      setActive({ kind: 'prompt', opts });
    });
  }, []);

  // Cancel must resolve falsy — never the collected values.
  const onCancel = useCallback(() => {
    settle(active?.kind === 'confirm' ? false : null);
  }, [active, settle]);

  const missing = useMemo(
    () =>
      active?.kind === 'prompt'
        ? active.opts.fields.filter((f) => f.required && !(values[f.name] || '').trim()).map((f) => f.name)
        : [],
    [active, values],
  );

  const onSubmit = useCallback(() => {
    if (!active) return;
    if (active.kind === 'confirm') { settle(true); return; }
    if (missing.length) { setTouched(true); return; }
    // Trim so a whitespace-only optional field reads as empty downstream.
    const out: Record<string, string> = {};
    active.opts.fields.forEach((f) => { out[f.name] = (values[f.name] ?? '').trim(); });
    settle(out);
  }, [active, missing, values, settle]);

  const isDanger = active?.opts && 'danger' in active.opts ? active.opts.danger : false;

  return (
    <DialogContext.Provider value={{ confirm, prompt, info }}>
      {children}
      {active && (
        <Modal
          isOpen
          onClose={onCancel}
          title={active.opts.title}
          size={active.kind === 'prompt' && active.opts.fields.length > 2 ? 'lg' : 'md'}
          footer={
            <div className="flex items-center justify-end gap-2">
              {!(active.kind === 'confirm' && active.opts.hideCancel) && (
                <button type="button" onClick={onCancel} className={`${btnBase} bg-wiz-card text-gray-300 border border-wiz-border hover:text-white`}>
                  {active.opts.cancelLabel || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                onClick={onSubmit}
                className={`${btnBase} text-white ${isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-wiz-purple hover:brightness-110'}`}
              >
                {active.kind === 'confirm'
                  ? active.opts.confirmLabel || 'Confirm'
                  : active.opts.submitLabel || 'Submit'}
              </button>
            </div>
          }
        >
          <form
            onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
            className="space-y-4"
          >
            {active.opts.message && (
              <div className="flex items-start gap-2 text-sm text-gray-300">
                {isDanger && <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" aria-hidden="true" />}
                <div className="min-w-0">{active.opts.message}</div>
              </div>
            )}

            {active.kind === 'prompt' &&
              active.opts.fields.map((f) => {
                const invalid = touched && f.required && !(values[f.name] || '').trim();
                const cls = `w-full px-3 py-2 rounded-lg bg-wiz-darker border text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-wiz-purple/40 ${
                  invalid ? 'border-red-500/60' : 'border-wiz-border'
                }`;
                const set = (v: string) => setValues((p) => ({ ...p, [f.name]: v }));
                return (
                  <div key={f.name}>
                    <label htmlFor={`dlg-${f.name}`} className="block text-xs font-medium text-gray-400 mb-1">
                      {f.label}
                      {f.required && <span className="text-red-400 ml-0.5">*</span>}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea
                        id={`dlg-${f.name}`} rows={4} className={cls} placeholder={f.placeholder}
                        value={values[f.name] ?? ''} onChange={(e) => set(e.target.value)}
                      />
                    ) : f.type === 'select' ? (
                      <select id={`dlg-${f.name}`} className={cls} value={values[f.name] ?? ''} onChange={(e) => set(e.target.value)}>
                        {(f.options || []).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`dlg-${f.name}`} type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
                        className={cls} placeholder={f.placeholder} autoFocus={active.opts.fields[0].name === f.name}
                        value={values[f.name] ?? ''} onChange={(e) => set(e.target.value)}
                      />
                    )}
                    {f.hint && <p className="mt-1 text-xs text-gray-500">{f.hint}</p>}
                    {invalid && <p className="mt-1 text-xs text-red-400">{f.label} is required.</p>}
                  </div>
                );
              })}
            {/* Enter-to-submit without a visible duplicate button. */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        </Modal>
      )}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
